import type { BridgeDoctorOption, DoctorMapping, DoctorMappingAction, OldDoctorMapping } from "@common/lib/api/bridge";

export type DoctorChoice = { kind: "map"; bridgeDoctorId: string } | { kind: "archive" } | { kind: "unset" };

const ARCHIVE = "__archive";
const UNSET = "";

/** For each bridge provider, the old doctor id that stays as the survivor when several rows share it. */
export type KeepChoices = Record<string, string>;

const initialChoice = (mapping: DoctorMapping, d: OldDoctorMapping): DoctorChoice => {
    // A decision recorded while the cutover is pending wins over the suggestion.
    const pending = d.pendingAction;
    if (pending?.action === "map") return { kind: "map", bridgeDoctorId: pending.bridgeDoctorId };
    if (pending?.action === "archive") return { kind: "archive" };
    if (pending?.action === "fold") {
        if (mapping.bridgeDoctors.some((b) => b.merged && b.doctorId === pending.intoDoctorId)) {
            return { kind: "map", bridgeDoctorId: pending.intoDoctorId };
        }
        const target = mapping.oldDoctors.find((o) => o.doctorId === pending.intoDoctorId);
        if (target?.pendingAction?.action === "map") {
            return { kind: "map", bridgeDoctorId: target.pendingAction.bridgeDoctorId };
        }
        return { kind: "unset" };
    }
    return d.suggestedBridgeDoctorId ? { kind: "map", bridgeDoctorId: d.suggestedBridgeDoctorId } : { kind: "unset" };
};

export const initialChoices = (mapping: DoctorMapping): Record<string, DoctorChoice> =>
    Object.fromEntries(mapping.oldDoctors.map((d): [string, DoctorChoice] => [d.doctorId, initialChoice(mapping, d)]));

/** Re-seeds the survivor of a stored map + fold group. Bitwerx-only: ezyVet mode never stores a fold. */
export const initialKeep = (mapping: DoctorMapping): KeepChoices => {
    const keep: KeepChoices = {};
    for (const d of mapping.oldDoctors) {
        if (d.pendingAction?.action !== "map") continue;
        const keeperId = d.doctorId;
        const folded = mapping.oldDoctors.some(
            (o) => o.pendingAction?.action === "fold" && o.pendingAction.intoDoctorId === keeperId,
        );
        if (folded) keep[d.pendingAction.bridgeDoctorId] = keeperId;
    }
    return keep;
};

// Any script's letters (Unicode property escape; the constructor form because tsconfig's target predates the literal `u` flag).
const NON_LETTERS = new RegExp("[^\\p{L}]+", "u");

const nameWords = (name: string): Set<string> =>
    new Set(
        name
            .toLowerCase()
            .split(NON_LETTERS)
            .filter((w) => w.length >= 3),
    );

/**
 * The row that should survive when `doctorIds` all chose `bridgeDoctorId`: one that still syncs
 * (the survivor keeps its own `sync_excluded`, and an excluded survivor drops the provider's bookings),
 * then a code candidate, then a shared name word with the provider, then more recent visits, then name order.
 * Only a default: every row in the group chose this one provider, so all of their visits end on the
 * same doctor whichever row is kept. The keeper decides only the surviving name and document-type
 * choices, and the "Keep" select shows it and lets the clinic change it before saving.
 */
export const defaultKeeper = (mapping: DoctorMapping, bridgeDoctorId: string, doctorIds: string[]): string => {
    const provider = mapping.bridgeDoctors.find((b) => b.doctorId === bridgeDoctorId);
    const providerWords = nameWords(provider?.name ?? "");
    const rank = (id: string) => {
        const d = mapping.oldDoctors.find((o) => o.doctorId === id);
        return {
            synced: d?.syncExcluded ? 0 : 1,
            candidate: d?.candidates.includes(bridgeDoctorId) ? 1 : 0,
            shared: Array.from(nameWords(d?.name ?? "")).some((w) => providerWords.has(w)) ? 1 : 0,
            visits: d?.recentVisits ?? 0,
            name: d?.name ?? "",
        };
    };
    return [...doctorIds].sort((x, y) => {
        const a = rank(x);
        const b = rank(y);
        return (
            b.synced - a.synced ||
            b.candidate - a.candidate ||
            b.shared - a.shared ||
            b.visits - a.visits ||
            a.name.localeCompare(b.name)
        );
    })[0];
};

const choosers = (mapping: DoctorMapping, choices: Record<string, DoctorChoice>, bridgeDoctorId: string): string[] =>
    mapping.oldDoctors
        .filter((d) => {
            const c = choices[d.doctorId];
            return c?.kind === "map" && c.bridgeDoctorId === bridgeDoctorId;
        })
        .map((d) => d.doctorId);

export const keeperFor = (
    mapping: DoctorMapping,
    choices: Record<string, DoctorChoice>,
    keep: KeepChoices,
    bridgeDoctorId: string,
): string => {
    const ids = choosers(mapping, choices, bridgeDoctorId);
    // The clinic's pick stands while that row still chooses this provider; only a pick that left the group falls back.
    const kept = keep[bridgeDoctorId];
    return kept !== undefined && ids.includes(kept) ? kept : defaultKeeper(mapping, bridgeDoctorId, ids);
};

/**
 * Stores the shown default for every provider several rows chose and no valid pick covers, so a later
 * refetch (new visit counts, a failed activity read) cannot re-rank the group and swap the keeper unasked.
 * Returns `keep` itself when nothing needs pinning. Bitwerx-only in practice: ezyVet mode's UI disables
 * an already-taken provider in other rows, so no ezyVet provider ever has more than one chooser.
 */
export const pinKeepers = (
    mapping: DoctorMapping,
    choices: Record<string, DoctorChoice>,
    keep: KeepChoices,
): KeepChoices => {
    let next = keep;
    for (const b of mapping.bridgeDoctors) {
        if (b.merged) continue;
        const ids = choosers(mapping, choices, b.doctorId);
        if (ids.length < 2 || ids.includes(keep[b.doctorId])) continue;
        next = { ...next, [b.doctorId]: defaultKeeper(mapping, b.doctorId, ids) };
    }
    return next;
};

/**
 * ezyVet mode never produces a "fold" action: its UI disables an already-chosen provider in other
 * rows instead of offering to combine (the backend's fold validation is bitwerx-specific — see
 * bridgeDoctorMapping.ts), so `keeperFor`/`provider.merged` are always moot for it — every ezyVet
 * provider has at most one chooser. Bitwerx: a choice onto a merged row, or onto a provider more
 * than one row chose, becomes a fold into the (merged row, or group's keeper) target instead of a map.
 */
export const choicesToActions = (
    mapping: DoctorMapping,
    choices: Record<string, DoctorChoice>,
    keep: KeepChoices,
): DoctorMappingAction[] =>
    mapping.oldDoctors.flatMap((d): DoctorMappingAction[] => {
        const doctorId = d.doctorId;
        const c = choices[doctorId];
        if (c?.kind === "archive") return [{ doctorId, action: "archive" }];
        if (c?.kind !== "map") return [];
        // Keyed off the ezyVet case explicitly (not `!== "bitwerx"`) so a mapping response from an
        // API-Server that hasn't shipped `mode` yet (independent-repo deploy ordering) falls back to
        // the existing, tested bitwerx fold behavior instead of silently losing it.
        if (mapping.mode === "ezyvet") return [{ doctorId, action: "map", bridgeDoctorId: c.bridgeDoctorId }];
        const provider = mapping.bridgeDoctors.find((b) => b.doctorId === c.bridgeDoctorId);
        if (provider?.merged) return [{ doctorId, action: "fold", intoDoctorId: provider.doctorId }];
        const keeper = keeperFor(mapping, choices, keep, c.bridgeDoctorId);
        if (keeper === doctorId) return [{ doctorId, action: "map", bridgeDoctorId: c.bridgeDoctorId }];
        return [{ doctorId, action: "fold", intoDoctorId: keeper }];
    });

export const unresolvedBlocking = (mapping: DoctorMapping, choices: Record<string, DoctorChoice>): OldDoctorMapping[] =>
    mapping.oldDoctors.filter((d) => d.blocking && (choices[d.doctorId]?.kind ?? "unset") === "unset");

const valueOf = (c: DoctorChoice | undefined): string => {
    if (c?.kind === "map") return c.bridgeDoctorId;
    if (c?.kind === "archive") return ARCHIVE;
    return UNSET;
};

const optionLabel = (b: BridgeDoctorOption) =>
    b.detail ? `${b.name} — ${b.detail} (#${b.pmsEmployeeId})` : `${b.name} (#${b.pmsEmployeeId})`;

const joinNames = (names: string[]): string =>
    names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * EN-997. One row per old doctor: pick the PIMS provider it is (Cornerstone for Bitwerx-era rows,
 * ezyVet for existing doctors being merged into synced providers), or archive it.
 * Candidates are listed first, then every other provider (the escape hatch for a row we could not
 * match at all). EN-1431 (bitwerx only): several rows may share a provider; one is kept and the
 * rest fold into it. ezyVet mode instead disables an already-chosen provider in the other rows — it
 * has no fold support, and unclaimed providers already exist as doctors there, so the "new doctors"
 * list and the appointments line are bitwerx-only too.
 */
export default function DoctorMappingTable({
    mapping,
    choices,
    keep,
    onChange,
    onKeepChange,
    disabled,
}: {
    mapping: DoctorMapping;
    choices: Record<string, DoctorChoice>;
    keep: KeepChoices;
    onChange: (doctorId: string, choice: DoctorChoice) => void;
    onKeepChange: (bridgeDoctorId: string, doctorId: string) => void;
    disabled?: boolean;
}) {
    // `!== "ezyvet"` (not `=== "bitwerx"`): an absent/undefined `mode` (e.g. a frontend deploy that
    // outpaces API-Server's rollout of this field) must default to the existing bitwerx behavior,
    // not silently switch every live bitwerx clinic to the untested ezyVet path.
    const isBitwerx = mapping.mode !== "ezyvet";
    const nameOf = (id: string) => mapping.oldDoctors.find((o) => o.doctorId === id)?.name || "(no name)";
    const takenBy = new Map<string, string>();
    for (const [doctorId, c] of Object.entries(choices)) if (c.kind === "map") takenBy.set(c.bridgeDoctorId, doctorId);
    const chosen = mapping.bridgeDoctors.map((b) => ({ b, ids: choosers(mapping, choices, b.doctorId) }));
    const unclaimed = chosen.filter(({ b, ids }) => !b.merged && ids.length === 0).map(({ b }) => b);
    const combined = isBitwerx ? chosen.filter(({ b, ids }) => !b.merged && ids.length > 1) : [];
    const addedToMerged = isBitwerx ? chosen.filter(({ b, ids }) => b.merged && ids.length > 0) : [];
    // A row whose candidates are all merged rows is offered those as fold targets, so the "more than one possible match" note is hidden for it. Always empty in ezyVet mode (no merged rows there).
    const mergedIds = new Set(mapping.bridgeDoctors.filter((b) => b.merged).map((b) => b.doctorId));

    return (
        <div className="flex w-full flex-col gap-4">
            <table className="w-full text-left text-base">
                <thead>
                    <tr className="text-sm font-semibold text-shadow-500">
                        <th className="py-2 pr-4">Doctor in HappyDoc</th>
                        <th className="py-2">{mapping.pimsName} provider</th>
                    </tr>
                </thead>
                <tbody>
                    {mapping.oldDoctors.map((d) => {
                        const ordered = [
                            ...mapping.bridgeDoctors.filter((b) => d.candidates.includes(b.doctorId)),
                            ...mapping.bridgeDoctors.filter((b) => !d.candidates.includes(b.doctorId)),
                        ];
                        return (
                            <tr key={d.doctorId} className="border-t border-shadow-100">
                                <td className="py-3 pr-4 align-top">
                                    <label
                                        htmlFor={`doctor-map-${d.doctorId}`}
                                        className="block font-semibold text-shadow-700"
                                    >
                                        {d.name || "(no name)"}
                                    </label>
                                    <div className="text-sm text-shadow-300">
                                        {isBitwerx && (
                                            <span>
                                                {d.appearsOnAppointments
                                                    ? "Needed for appointments"
                                                    : "Not used on appointments"}
                                            </span>
                                        )}
                                        {d.classification === "ambiguous" &&
                                            d.candidates.length > 0 &&
                                            !d.candidates.every((id) => mergedIds.has(id)) && (
                                                <span> · more than one possible match</span>
                                            )}
                                    </div>
                                </td>
                                <td className="py-3 align-top">
                                    <select
                                        id={`doctor-map-${d.doctorId}`}
                                        className="w-full rounded-md border border-shadow-100 bg-white px-2 py-2 text-base text-shadow-700"
                                        disabled={disabled}
                                        value={valueOf(choices[d.doctorId])}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === ARCHIVE) onChange(d.doctorId, { kind: "archive" });
                                            else if (v === UNSET) onChange(d.doctorId, { kind: "unset" });
                                            else onChange(d.doctorId, { kind: "map", bridgeDoctorId: v });
                                        }}
                                    >
                                        <option value={UNSET}>Choose…</option>
                                        {ordered.map((b) => (
                                            <option
                                                key={b.doctorId}
                                                value={b.doctorId}
                                                disabled={
                                                    !isBitwerx &&
                                                    takenBy.has(b.doctorId) &&
                                                    takenBy.get(b.doctorId) !== d.doctorId
                                                }
                                            >
                                                {optionLabel(b)}
                                            </option>
                                        ))}
                                        <option value={ARCHIVE}>Archive — not a provider</option>
                                    </select>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {mapping.oldDoctors.some((d) => d.pendingAction) && (
                <p className="text-base text-shadow-500">
                    Saved. These changes apply when the switch to HappyBridge completes.
                </p>
            )}
            {combined.map(({ b, ids }) => (
                <div key={b.doctorId} className="text-base text-shadow-500">
                    <span>{joinNames(ids.map(nameOf))} will be combined into one doctor. Keep: </span>
                    <select
                        aria-label={`Doctor to keep for ${b.name}`}
                        className="rounded-md border border-shadow-100 bg-white px-2 py-1 text-base text-shadow-700"
                        disabled={disabled}
                        value={keeperFor(mapping, choices, keep, b.doctorId)}
                        onChange={(e) => onKeepChange(b.doctorId, e.target.value)}
                    >
                        {ids.map((id) => (
                            <option key={id} value={id}>
                                {nameOf(id)}
                            </option>
                        ))}
                    </select>
                </div>
            ))}
            {addedToMerged.map(({ b, ids }) => (
                <p key={b.doctorId} className="text-base text-shadow-500">
                    {joinNames(ids.map(nameOf))} will be added to {b.name}.
                </p>
            ))}
            {isBitwerx && unclaimed.length > 0 && (
                <div className="text-base text-shadow-500">
                    <p>These {mapping.pimsName} providers will be added as new doctors:</p>
                    <ul className="list-disc pl-5">
                        {unclaimed.map((b) => (
                            <li key={b.doctorId}>{b.name}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
